import theme from "@utils/theme";
import {
  ArrowUpRight,
  Package,
  ShoppingCart,
  Utensils,
  Clock,
  User,
  Truck,
  MapPin,
  CheckCircle2,
  TrendingUp,
  BarChart3,
  DollarSign,
  Star,
  Calendar,
  Zap,
  Shield,
  Target,
} from "lucide-react";
import {
  ActivityCard,
  ActivityContent,
  ActivityHeader,
  ActivityIcon,
  ActivityItem,
  ActivityList,
  ActivityText,
  ActivityTime,
  ActivityTitle,
  AgentAvatar,
  AgentDetails,
  AgentInfo,
  AgentName,
  AgentRating,
  AgentStats,
  BottomSection,
  CustomerMetrics,
  DashboardWrapper,
  HeaderArea,
  HeaderText,
  InfoPanel,
  MainDashboard,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricItem,
  MetricItemContent,
  MetricItemIcon,
  MetricItemLabel,
  MetricItemValue,
  MetricLabel,
  MetricsGrid,
  MetricTrend,
  MetricValue,
  PanelHeader,
  PanelTitle,
  PerformanceCard,
  PerformanceGrid,
  PerformanceIcon,
  PerformanceLabel,
  PerformanceTrend,
  PerformanceValue,
  QuickStats,
  SectionHeader,
  SectionTitle,
  ServiceCard,
  ServiceIcon,
  ServiceName,
  ServicesSection,
  ServiceStats,
  ServiceSubtext,
  StatItem,
  StatLabel,
  StatValue,
  ViewAll,
  DateFilter,
  DeliveryItem,
  DeliveryList,
} from "./style";
import Grid from "@components/grid";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dashboard as DashboardApi, Order as OrderApi } from "@apis/index";
import { RootState } from "@store/index";

const LoadingPulse = ({ width = "100%", height = "20px" }: { width?: string | number, height?: string | number }) => (
  <div style={{
    width,
    height,
    background: "#f3f4f6",
    borderRadius: "4px",
    animation: "pulse 1.5s infinite"
  }} />
);

// ==================== MAIN COMPONENT ====================
const DeliveryAdminDashboard = () => {
  const navigate = useNavigate();
  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: dashboard, loading: dashboardLoading } = useSelector(
    (state: RootState) => state.dashboard,
  );
  const { data: topPerformers, loading: topPerformersLoading } = useSelector(
    (state: RootState) => state.topPerformers,
  );

  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [orderStatusStats, setOrderStatusStats] = useState<any>({});
  const [loadingExtras, setLoadingExtras] = useState(true);

  // Unified Date Filter State
  const [dateFilter, setDateFilter] = useState("All Time");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [apiDates, setApiDates] = useState({ startDate: "", endDate: "" });

  const filterOptions = [
    "All Time",
    "Today",
    "This Week",
    "This Month",
    "This Year",
    "Custom Time",
  ];

  // Update dates for API calls
  useEffect(() => {
    const today = new Date();
    let startDate = "";
    let endDate = "";

    if (dateFilter === "Today") {
      startDate = today.toISOString().split("T")[0];
      endDate = today.toISOString().split("T")[0];
    } else if (dateFilter === "This Week") {
      const first = today.getDate() - today.getDay();
      const last = first + 6;
      const firstDay = new Date(today.getFullYear(), today.getMonth(), first);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), last);
      startDate = firstDay.toISOString().split("T")[0];
      endDate = lastDay.toISOString().split("T")[0];
    } else if (dateFilter === "This Month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      startDate = firstDay.toISOString().split("T")[0];
      endDate = lastDay.toISOString().split("T")[0];
    } else if (dateFilter === "This Year") {
      const firstDay = new Date(today.getFullYear(), 0, 1);
      const lastDay = new Date(today.getFullYear(), 11, 31);
      startDate = firstDay.toISOString().split("T")[0];
      endDate = lastDay.toISOString().split("T")[0];
    } else if (dateFilter === "Custom Time") {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    if (dateFilter !== "Custom Time" || (startDate && endDate)) {
      setApiDates({ startDate, endDate });
    }
  }, [dateFilter, customStartDate, customEndDate]);

  const fetchData = async () => {
    try {
      setLoadingExtras(true);
      const params = {
        startDate: apiDates.startDate,
        endDate: apiDates.endDate,
      };

      await Promise.all([
        DashboardApi.summary({ state: "dashboard", params }),
        DashboardApi.topPerformers({
          state: "topPerformers",
          params: { ...params, limit: 5 },
        }),
        OrderApi.list({ params: { ...params, limit: 5 } }).then((res) =>
          setRecentOrders(res.data || []),
        ),
        DashboardApi.orderStatusStats({ params }).then((res) =>
          setOrderStatusStats(res || {}),
        ),
      ]);
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoadingExtras(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiDates]);

  const metrics = [
    {
      label: "Total Orders",
      value: dashboard?.orders?.total?.toLocaleString() || "0",
      trend: dashboard?.orders?.new || 0,
      positive: true,
      icon: ShoppingCart,
      background: theme.orange.orange50,
      color: theme.orange.orange500,
      link: "/admin/orders/all",
    },
    {
      label: "Active Deliveries",
      value: dashboard?.activeDeliveries?.total?.toLocaleString() || "0",
      trend: 0,
      positive: true,
      icon: Truck,
      background: "#E9FFF1",
      color: "#22C55E",
      link: "/admin/dispatch",
    },
    {
      label: "Revenue",
      value: `रू ${dashboard?.revenue?.total?.toLocaleString() || "0"}`,
      trend: dashboard?.revenue?.trend || 0,
      positive: dashboard?.revenue?.trend >= 0,
      icon: DollarSign,
      background: "#DBEAFE",
      color: "#3B82F6",
      link: "/admin/transaction/transaction-report",
    },
    {
      label: "Satisfaction",
      value: `${dashboard?.satisfaction?.percentage || 0}%`,
      trend: dashboard?.satisfaction?.average || 0,
      positive: true,
      icon: Star,
      background: "#FEF0CD",
      color: "#E2AF2C",
      link: "/admin/audit-logs",
    },
  ];

  const serviceModules = (dashboard?.moduleStats || []).map((m: any) => {
    let icon = ShoppingCart;
    const name = m.moduleName?.toLowerCase() || "";
    
    if (name.includes("food")) icon = MapPin;
    else if (name.includes("pharm") || name.includes("medi")) icon = Package;
    else if (name.includes("grocer")) icon = Utensils; // or Warehouse/Store
    else if (name.includes("store")) icon = Utensils;
    else if (name.includes("dine")) icon = Star;
    else if (name.includes("any")) icon = Zap;

    const foundModule = modules?.find(
      (mod: any) => mod.name.toLowerCase() === name
    );

    return {
      id: foundModule?.id || m.moduleId,
      name: m.moduleName,
      orders: m.orderCount,
      icon,
      subtext: `${m.orderCount} ${m.orderCount === 1 ? "order" : "orders"} placed`,
    };
  });

  const customerMetrics = [
    { label: "Total Customers", value: dashboard?.customers?.total?.toLocaleString() || "0", icon: User, link: "/admin/users/customer/customer-lists" },
    { label: "New (24h)", value: dashboard?.customers?.new?.toString() || "0", icon: TrendingUp, link: "/admin/users/customer/customer-lists" },
    { label: "Satisfaction", value: `${dashboard?.satisfaction?.average || 0}/5`, icon: Zap },
    { label: "Total Items", value: dashboard?.productStats?.total?.toLocaleString() || "0", icon: Package, link: "/admin/product-management/products" },
  ];

  const performanceMetrics = [
    {
      label: "Wait List",
      value: orderStatusStats?.["Order Placed"] || 0,
      trend: 0,
      positive: true,
      icon: Clock,
      background: "#E9FFF1",
      color: "#22C55E",
      link: "/admin/orders/order-placed",
    },
    {
      label: "Processing",
      value: orderStatusStats?.["Order Processing"] || 0,
      trend: 0,
      positive: true,
      icon: Target,
      background: "#DBEAFE",
      color: "#3B82F6",
      link: "/admin/orders/order-processing",
    },
    {
      label: "Satisfaction",
      value: `${dashboard?.satisfaction?.percentage || 0}%`,
      trend: 0,
      positive: true,
      icon: Star,
      background: "#FEF0CD",
      color: "#E2AF2C",
      link: "/admin/audit-logs",
    },
    {
      label: "Safe Delivery",
      value: "100%",
      trend: 0,
      positive: true,
      icon: Shield,
      background: "#FFEDD5",
      color: theme.orange.orange500,
    },
  ];

  const quickStats = [
    { label: "Confirmed Orders", value: (orderStatusStats?.["Order Confirmed"] || 0).toString(), link: "/admin/orders/order-confirmed" },
    { label: "Out For Delivery", value: (orderStatusStats?.["Out for Delivery"] || 0).toString(), link: "/admin/orders/out-for-delivery" },
    { label: "Total Delivered", value: (orderStatusStats?.["Delivered"] || 0).toString(), link: "/admin/orders/delivered" },
    { label: "Cancelled", value: (orderStatusStats?.["Cancelled"] || orderStatusStats?.["Canceled"] || 0).toString(), link: "/admin/orders/cancelled" },
  ];

  return (
    <DashboardWrapper>
      {/* Header */}
      <HeaderArea style={{ gap: "15px", flexWrap: "wrap" }}>
        <HeaderText>
          <h1>Dashboard Overview</h1>
          <p>Monitor your delivery services and performance</p>
        </HeaderText>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#fff",
              padding: "4px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              gap: "4px",
            }}
          >
            {filterOptions.map((option) => (
              <div
                key={option}
                onClick={() => setDateFilter(option)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: dateFilter === option ? "600" : "400",
                  background:
                    dateFilter === option ? theme.orange.orange50 : "transparent",
                  color:
                    dateFilter === option ? theme.orange.orange600 : "#6b7280",
                  cursor: "pointer",
                  border:
                    dateFilter === option
                      ? `1px solid ${theme.orange.orange200}`
                      : "1px solid transparent",
                  transition: "all 0.2s",
                }}
              >
                {option}
              </div>
            ))}
          </div>

          {dateFilter === "Custom Time" && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  fontSize: "12px",
                  outline: "none",
                }}
              />
              <span style={{ fontSize: "12px", color: "#9ca3af" }}>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  fontSize: "12px",
                  outline: "none",
                }}
              />
            </div>
          )}
        </div>
      </HeaderArea>

      {/* Key Metrics */}
      <Grid>
        {metrics.map((metric, index) => (
          <MetricCard key={index} onClick={() => metric.link && navigate(metric.link)}>
            <MetricHeader>
              <MetricIcon background={metric.background} color={metric.color}>
                <metric.icon size={18} />
              </MetricIcon>
              <MetricTrend positive={metric.positive}>
                <ArrowUpRight size={12} />
                {metric.trend}%
              </MetricTrend>
            </MetricHeader>
            <MetricValue>{dashboardLoading ? <LoadingPulse width={60} /> : metric.value}</MetricValue>
            <MetricLabel>{metric.label}</MetricLabel>
          </MetricCard>
        ))}
      </Grid>

      {/* Services */}
      <ServicesSection>
        <SectionHeader>
          <SectionTitle>Service Modules</SectionTitle>
          <ViewAll onClick={() => navigate("/admin/modules/list")}>View all</ViewAll>
        </SectionHeader>
        <Grid>
          {serviceModules?.length > 0 ? serviceModules.map((service: any, index: number) => (
            <ServiceCard key={index} onClick={() => service.id && navigate(`/admin/dashboard?module=${service.name}&moduleId=${service.id}`)}>
              <ServiceIcon>
                <service.icon size={16} />
              </ServiceIcon>
              <ServiceName>{service.name}</ServiceName>
              <ServiceStats>{service.orders}</ServiceStats>
              <ServiceSubtext>{service.subtext}</ServiceSubtext>
            </ServiceCard>
          )) : (
            [1, 2, 3, 4].map((_, i) => <LoadingPulse key={i} height={120} />)
          )}
        </Grid>
      </ServicesSection>

      {/* Main Content */}
      <MainDashboard>
        <ActivityCard>
          <ActivityHeader>
            <ActivityTitle>Recent Orders</ActivityTitle>
            <Clock size={16} color="#6b7280" />
          </ActivityHeader>
          <ActivityList>
            {recentOrders.length > 0 ? recentOrders.map((order, index) => (
              <ActivityItem key={index}>
                <ActivityIcon>
                  <Package size={14} />
                </ActivityIcon>
                <ActivityContent>
                  <ActivityText>Order #{order.id} - {order.status}</ActivityText>
                  <ActivityTime>{new Date(order.createdAt).toLocaleString()}</ActivityTime>
                </ActivityContent>
              </ActivityItem>
            )) : (
              <ActivityText style={{ padding: "20px", textAlign: "center", color: "#9ca3af" }}>
                No recent activity
              </ActivityText>
            )}
          </ActivityList>
        </ActivityCard>

        <InfoPanel>
          <PanelHeader>
            <PanelTitle>Top Deliverymen</PanelTitle>
            <User size={16} color="#6b7280" />
          </PanelHeader>
          <DeliveryList>
            {topPerformers?.topDeliverymen?.length > 0 ? topPerformers.topDeliverymen.map((agent: any, index: number) => (
              <DeliveryItem key={index}>
                <AgentInfo>
                  <AgentAvatar>{agent.name?.charAt(0)}</AgentAvatar>
                  <AgentDetails>
                    <AgentName>{agent.name}</AgentName>
                    <AgentStats>{agent.orderCount} deliveries</AgentStats>
                  </AgentDetails>
                </AgentInfo>
                <AgentRating>★ 4.9</AgentRating>
              </DeliveryItem>
            )) : (
              <ActivityText style={{ padding: "20px", textAlign: "center", color: "#9ca3af" }}>
                No data available
              </ActivityText>
            )}
          </DeliveryList>
        </InfoPanel>
      </MainDashboard>

      {/* Bottom Section */}
      <BottomSection>
        <InfoPanel>
          <PanelHeader>
            <PanelTitle>Performance Metrics</PanelTitle>
            <TrendingUp size={16} color="#6b7280" />
          </PanelHeader>
          <PerformanceGrid>
            {performanceMetrics.map((item, index) => (
              <PerformanceCard key={index} onClick={() => item.link && navigate(item.link)}>
                <PerformanceIcon
                  background={item.background}
                  color={item.color}
                >
                  <item.icon size={18} />
                </PerformanceIcon>
                <PerformanceValue>{loadingExtras ? <LoadingPulse width={40} /> : item.value}</PerformanceValue>
                <PerformanceLabel>{item.label}</PerformanceLabel>
                <PerformanceTrend positive={item.positive}>
                  {item.trend > 0 && "+"}
                  {item.trend}
                </PerformanceTrend>
              </PerformanceCard>
            ))}
          </PerformanceGrid>
        </InfoPanel>

        <InfoPanel>
          <PanelHeader>
            <PanelTitle>Platform Insights</PanelTitle>
            <User size={16} color="#6b7280" />
          </PanelHeader>
          <CustomerMetrics>
            {customerMetrics.map((metric, index) => (
              <MetricItem key={index} onClick={() => metric.link && navigate(metric.link)}>
                <MetricItemIcon>
                  <metric.icon size={16} />
                </MetricItemIcon>
                <MetricItemContent>
                  <MetricItemLabel>{metric.label}</MetricItemLabel>
                  <MetricItemValue>{dashboardLoading ? <LoadingPulse width={50} /> : metric.value}</MetricItemValue>
                </MetricItemContent>
              </MetricItem>
            ))}
          </CustomerMetrics>
        </InfoPanel>

        <InfoPanel>
          <PanelHeader>
            <PanelTitle>Order Overview</PanelTitle>
            <Clock size={16} color="#6b7280" />
          </PanelHeader>
          <QuickStats>
            {quickStats.map((stat, index) => (
              <StatItem key={index} onClick={() => stat.link && navigate(stat.link)}>
                <StatLabel>{stat.label}</StatLabel>
                <StatValue>{loadingExtras ? <LoadingPulse width={30} /> : stat.value}</StatValue>
              </StatItem>
            ))}
          </QuickStats>
        </InfoPanel>
      </BottomSection>
    </DashboardWrapper>
  );
};

export default DeliveryAdminDashboard;
